import React from 'react';
import { Link } from 'react-router-dom';
import { FaPills, FaFlask, FaLeaf, FaCapsules, FaArrowRight } from 'react-icons/fa';
import EnquiryForm from '../components/EnquiryForm';

const ThirdPartyManufacturing = () => {
  const services = [
    {
      icon: FaPills,
      title: 'Private Label Manufacturing',
      description: 'Launch your brand with our white-label products. Choose from 68+ ready formulations or customize your own.',
      image: 'https://images.unsplash.com/photo-1641561421178-db8542057811?w=400&q=80'
    },
    {
      icon: FaFlask,
      title: 'Custom Formulation Development',
      description: 'Work with our R&D team to develop unique formulations tailored to your brand vision and market needs.',
      image: 'https://images.unsplash.com/photo-1758345680670-20a895a2dba3?w=400&q=80'
    },
    {
      icon: FaLeaf,
      title: 'Bulk Contract Manufacturing',
      description: 'Large-scale production with consistent quality. Perfect for established brands looking to scale.',
      image: 'https://images.unsplash.com/photo-1671493234884-b1611bcf3e69?w=400&q=80'
    },
    {
      icon: FaCapsules,
      title: 'Complete Packaging Solutions',
      description: 'From bottle design to labeling, we handle all packaging needs with multiple format options.',
      image: 'https://images.unsplash.com/photo-1623834928030-2208b3081255?w=400&q=80'
    },
  ];

  const steps = [
    { number: '01', title: 'Initial Consultation', description: 'Discuss your brand vision, target market, and product requirements with our team.' },
    { number: '02', title: 'Product Selection', description: 'Choose from existing formulations or collaborate on custom development.' },
    { number: '03', title: 'Sampling & Testing', description: 'Receive product samples for quality assessment and market testing.' },
    { number: '04', title: 'Packaging Design', description: 'Finalize packaging, labeling, and branding elements for your products.' },
    { number: '05', title: 'Regulatory Approval', description: 'We assist with all necessary licenses, certifications, and compliance.' },
    { number: '06', title: 'Production', description: 'Manufacturing begins with strict quality control at every step.' },
    { number: '07', title: 'Delivery', description: 'Timely delivery of finished products ready for market distribution.' },
  ];

  return (
    <div className="min-h-screen" data-testid="third-party-manufacturing-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#0F5132] to-[#0F5132] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Third-Party Manufacturing Services</h1>
            <p className="text-xl text-gray-100 mb-8">
              Launch your Ayurvedic or Nutraceutical brand with India's trusted manufacturing partner. End-to-end solutions from formulation to delivery.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#enquiry-form"
                className="px-8 py-4 bg-[#0F5132] hover:bg-[#C9A227] text-white font-semibold rounded-lg transition-all duration-200 shadow-xl"
              >
                Request Manufacturing Quote
              </a>
              <Link
                to="/products"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-200 border-2 border-white/30"
              >
                View Product Catalogue
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-4">Our Manufacturing Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive third-party manufacturing solutions for startups, established brands, and distributors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="bg-[#0F5132] p-6 flex items-center justify-center h-40">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <service.icon className="text-[#0F5132] text-2xl" />
                    <h3 className="text-2xl font-bold text-[#0F5132]">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-4">7-Step Manufacturing Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From concept to completion - our streamlined process ensures quality at every stage
            </p>
          </div>

          <div className="relative">
            {/* Connecting Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#0F5132]/20 transform -translate-x-1/2"></div>

            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
                      <h3 className="text-2xl font-bold text-[#0F5132] mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-20 h-20 bg-[#0F5132] rounded-full flex items-center justify-center text-[#0F5132] text-2xl font-bold shadow-xl">
                      {step.number}
                    </div>
                  </div>
                  
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-6">
                Why Partner with Shivangel?
              </h2>
              <div className="space-y-4">
                {[
                  'ISO, GMP & AYUSH Certified Facility',
                  'Flexible MOQ - Start Small, Scale Fast',
                  '68+ Ready Formulations Available',
                  'Complete Regulatory Compliance Support',
                  'In-House Quality Testing Laboratory',
                  'Competitive Pricing & Timely Delivery',
                  '25+ Years Industry Experience',
                  'Dedicated Account Management',
                ].map((point, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#0F5132] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-[#0F5132] font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <img
                src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800&q=80"
                alt="Manufacturing Partnership"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-4">Ready to Start Manufacturing?</h2>
            <p className="text-lg text-gray-600">
              Fill out the form below and our team will contact you within 24 hours
            </p>
          </div>
          <EnquiryForm title="Request Manufacturing Quote" context="manufacturing" />
        </div>
      </section>
    </div>
  );
};

export default ThirdPartyManufacturing;